import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import { salvarBlob } from "../../services/resource/tiInventarioService";
import SeletorResponsavel from "./SeletorResponsavel";
import VisualizadorAnexo from "./VisualizadorAnexo";
import { formatarData, formatarDataHora, hojeIso } from "./tiUtils";
import { gerarTermoResponsabilidade } from "./termoResponsabilidadePdf";

const INPUT = "border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-full";

/**
 * Termos de responsabilidade do ativo: gera o PDF para assinatura, registra quem recebeu o equipamento
 * (com o documento assinado, quando houver) e permite consultar o documento associado a cada registro.
 */
export default function TermosAtivoModal({ visible, onHide, service, ativo, onAlterado }) {
    const [termos, setTermos] = useState([]);
    const [selecionadoId, setSelecionadoId] = useState(null);
    const [registrando, setRegistrando] = useState(false);
    const [form, setForm] = useState({ nome: "", cargo: "", data: hojeIso(), observacao: "" });
    const [arquivo, setArquivo] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [gerando, setGerando] = useState(false);
    const inputArquivo = useRef(null);
    const inputDocumento = useRef(null);
    const termoParaDocumento = useRef(null);

    const ativoId = ativo?.id;

    const carregar = useCallback(async (selecionarPrimeiro = false) => {
        if (!ativoId) return;
        const res = await service.listarTermos(ativoId);
        const lista = res.data || [];
        setTermos(lista);
        setSelecionadoId(atual => {
            if (selecionarPrimeiro && lista.length) return lista[0].id;
            return lista.some(t => t.id === atual) ? atual : (lista[0]?.id ?? null);
        });
    }, [ativoId, service]);

    useEffect(() => {
        if (!visible) return;
        setRegistrando(false);
        carregar(true).catch(() => setTermos([]));
    }, [visible, carregar]);

    const abrirRegistro = () => {
        setForm({
            nome: ativo?.responsavelNome || "",
            cargo: ativo?.responsavelCargo || "",
            data: hojeIso(),
            observacao: "",
        });
        setArquivo(null);
        setRegistrando(true);
    };

    const gerarPdf = async () => {
        setGerando(true);
        try {
            await gerarTermoResponsabilidade(ativo);
        } catch (e) {
            toast.error("Não foi possível gerar o termo de responsabilidade.");
        } finally {
            setGerando(false);
        }
    };

    const salvar = async () => {
        if (!form.nome.trim()) {
            toast.error("Informe quem recebeu o equipamento.");
            return;
        }
        if (!form.data) {
            toast.error("Informe a data do recebimento.");
            return;
        }
        setSalvando(true);
        try {
            await service.registrarTermo(ativoId, {
                recebedorNome: form.nome,
                recebedorCargo: form.cargo,
                dataRecebimento: form.data,
                observacao: form.observacao,
            }, arquivo);
            toast.success("Recebimento registrado com sucesso!");
            setRegistrando(false);
            await carregar(true);
            onAlterado?.();
        } catch (e) {
            // mensagem do backend exibida pelo interceptor
        } finally {
            setSalvando(false);
        }
    };

    const escolherDocumento = (termo) => {
        termoParaDocumento.current = termo.id;
        inputDocumento.current?.click();
    };

    const enviarDocumento = async (e) => {
        const arq = e.target.files?.[0];
        e.target.value = "";
        const termoId = termoParaDocumento.current;
        if (!arq || !termoId) return;
        try {
            await service.anexarDocumentoDoTermo(termoId, arq);
            toast.success("Documento associado ao termo.");
            await carregar();
            onAlterado?.();
        } catch (err) {
            // mensagem do backend exibida pelo interceptor
        }
    };

    const baixar = async (termo) => {
        try {
            const res = await service.baixarAnexo(termo.anexoId);
            salvarBlob(res, termo.anexoNome);
        } catch (e) {
            toast.error("Não foi possível baixar o documento.");
        }
    };

    const selecionado = termos.find(t => t.id === selecionadoId) || null;
    const anexoSelecionado = selecionado?.anexoId
        ? { id: selecionado.anexoId, nomeOriginal: selecionado.anexoNome }
        : null;

    return (
        <Dialog
            header={`Termos de responsabilidade — ${ativo?.nomenclatura || ativo?.patrimonio || `#${ativoId ?? ""}`}`}
            visible={visible}
            onHide={onHide}
            blockScroll
            className="w-[98%] md:w-[1100px]"
            contentClassName="pt-3"
        >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-4 border-b border-border">
                <p className="text-xs text-muted-foreground max-w-xl">
                    Gere o termo em PDF para assinatura e, depois, registre quem recebeu o equipamento anexando o
                    documento assinado. Cada registro fica no histórico do ativo.
                </p>
                <div className="flex gap-2">
                    <Button label="Gerar termo (PDF)" iconLeft="fa fa-file-contract" variant="white"
                        loading={gerando} onClick={gerarPdf} />
                    <Button label="Registrar recebimento" iconLeft="fa fa-user-check" variant="indigo"
                        disabled={ativo?.encerrado}
                        title={ativo?.encerrado ? "Ativos baixados/descartados não aceitam novo termo" : undefined}
                        onClick={abrirRegistro} />
                </div>
            </div>

            {registrando && (
                <section className="rounded-xl border border-border p-4 mb-4 bg-muted/20">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Registrar recebimento do equipamento</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                            <label className="block text-xs text-muted-foreground mb-1">Quem recebeu *</label>
                            <SeletorResponsavel
                                service={service}
                                nome={form.nome}
                                cargo={form.cargo}
                                onChange={({ nome, cargo }) => setForm(f => ({ ...f, nome, cargo }))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-muted-foreground mb-1">Cargo (opcional)</label>
                            <input className={INPUT} maxLength={150} value={form.cargo}
                                onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))} />
                        </div>
                        <div>
                            <label className="block text-xs text-muted-foreground mb-1">Data do recebimento *</label>
                            <input type="date" className={INPUT} max={hojeIso()} value={form.data}
                                onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs text-muted-foreground mb-1">Observação (opcional)</label>
                            <input className={INPUT} maxLength={500} value={form.observacao}
                                onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} />
                        </div>
                        <div className="md:col-span-3">
                            <label className="block text-xs text-muted-foreground mb-1">
                                Documento assinado (opcional — pode ser anexado depois)
                            </label>
                            <div className="flex items-center gap-2">
                                <input ref={inputArquivo} type="file" className="hidden"
                                    onChange={e => setArquivo(e.target.files?.[0] || null)} />
                                <Button label="Escolher arquivo" iconLeft="fa fa-paperclip" variant="white"
                                    onClick={() => inputArquivo.current?.click()} />
                                <span className="text-sm text-muted-foreground break-all">
                                    {arquivo ? arquivo.name : "Nenhum arquivo selecionado"}
                                </span>
                                {arquivo && (
                                    <button className="text-xs text-destructive hover:underline"
                                        onClick={() => { setArquivo(null); if (inputArquivo.current) inputArquivo.current.value = ""; }}>
                                        Remover
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button label="Cancelar" variant="gray" onClick={() => setRegistrando(false)} />
                        <Button label="Salvar registro" iconLeft="fa fa-check" variant="indigo" loading={salvando} onClick={salvar} />
                    </div>
                </section>
            )}

            <input ref={inputDocumento} type="file" className="hidden" onChange={enviarDocumento} />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                <div className="lg:col-span-2">
                    {termos.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nenhum recebimento registrado para este ativo.</p>
                    ) : (
                        <ul className="divide-y divide-border rounded-xl border border-border">
                            {termos.map(t => (
                                <li key={t.id} className={`p-3 text-sm ${t.id === selecionadoId ? "bg-primary/5" : ""}`}>
                                    <button className="text-left font-medium text-foreground hover:underline"
                                        onClick={() => setSelecionadoId(t.id)}>
                                        {t.recebedorNome}{t.recebedorCargo ? ` — ${t.recebedorCargo}` : ""}
                                    </button>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        Recebido em {formatarData(t.dataRecebimento)}
                                        {t.filialNome ? ` · ${t.filialNome}` : ""}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Registrado por {t.registradoPorNome || "—"} em {formatarDataHora(t.registradoEm)}
                                    </div>
                                    {t.responsavelNome && t.responsavelNome !== t.recebedorNome && (
                                        <div className="text-xs text-muted-foreground">
                                            Responsável no cadastro: {t.responsavelNome}
                                        </div>
                                    )}
                                    {t.observacao && <div className="text-xs mt-1">Obs.: {t.observacao}</div>}
                                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs">
                                        {t.anexoId ? (
                                            <>
                                                <button className="text-primary hover:underline" onClick={() => setSelecionadoId(t.id)}>
                                                    Ver documento
                                                </button>
                                                <button className="hover:underline" onClick={() => baixar(t)}>Baixar</button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-amber-700">Sem documento assinado</span>
                                                <button className="text-primary hover:underline" onClick={() => escolherDocumento(t)}>
                                                    Anexar documento
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="lg:col-span-3 rounded-xl border border-border p-3 min-h-[300px]">
                    {selecionado && !anexoSelecionado ? (
                        <div className="h-full min-h-[260px] flex items-center justify-center text-sm text-muted-foreground text-center p-6">
                            Este recebimento ainda não tem documento assinado associado.
                        </div>
                    ) : (
                        <VisualizadorAnexo service={service} anexo={anexoSelecionado} />
                    )}
                </div>
            </div>
        </Dialog>
    );
}
